import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Calendar, Activity, BarChart3, 
  ArrowUpRight, ArrowDownRight, RefreshCw, Layers,
  Download, X
} from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";

interface DailyData {
  date: string;
  pnl: number;
  fees?: number;
  net_pnl?: number;
  win_count: number;
  loss_count: number;
  trade_count: number;
  bots: {
    bot_id: number;
    name: string;
    symbol: string;
    pnl: number;
    fees?: number;
    net_pnl?: number;
    trade_count: number;
    win_count: number;
    loss_count: number;
    is_live: boolean;
    use_testnet?: boolean;
    archived?: boolean;
    disabled?: boolean;
  }[];
}

interface DailyStatsProps {
  onSelectBot?: (id: string) => void;
  disable_all_testnet?: boolean;
  userId?: number;
}

export const DailyStats: React.FC<DailyStatsProps> = ({ onSelectBot, disable_all_testnet, userId }) => {
  const isPrivate = useAppSelector((state) => state.ui.isPrivate);
  const MODE_KEY = 'daily_stats_mode';
  const MONTH_KEY = 'daily_stats_month';
  const SYMBOL_KEY = 'daily_stats_symbol';

  const [data, setData] = useState<DailyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportYear, setExportYear] = useState(() => String(new Date().getFullYear()));
  const [exportMonth, setExportMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, '0'));
  const [mode, setMode] = useState<'all' | 'mainnet' | 'testnet' | 'paper'>(() => {
    const saved = localStorage.getItem(MODE_KEY);
    if (saved === 'real') return 'mainnet';
    return (saved as any) || 'all';
  });
  const [selectedSymbol, setSelectedSymbol] = useState<string>(() => localStorage.getItem(SYMBOL_KEY) || 'all');
  const [availableSymbols, setAvailableSymbols] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const saved = localStorage.getItem(MONTH_KEY);
    if (saved) return saved;
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });

  useEffect(() => {
    localStorage.setItem(MODE_KEY, mode);
    localStorage.setItem(MONTH_KEY, selectedMonth);
    localStorage.setItem(SYMBOL_KEY, selectedSymbol);
  }, [mode, selectedMonth, selectedSymbol]);

  const fetchData = async () => {
    if (!navigator.onLine) return;
    setLoading(true);
    try {
      const res = await fetch(`/dca/daily_stats?mode=${mode}&month=${selectedMonth}&symbol=${selectedSymbol}${userId ? `&user_id=${userId}` : ''}`);
      const json = await res.json();
      const dailyData = json.daily_data || [];
      setData(dailyData);
      setAvailableSymbols(json.available_symbols || []);
      if (json.month) {
        setSelectedMonth(json.month);
      }
      if (dailyData.length > 0) {
        setSelectedDate((current) => {
          if (current && dailyData.some((day: DailyData) => day.date === current)) {
            return current;
          }

          return dailyData[dailyData.length - 1].date;
        });
      } else {
        setSelectedDate(null);
      }
    } catch (e) {
      if (navigator.onLine) {
        console.error("Failed to fetch daily stats", e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [mode, selectedMonth, selectedSymbol]);

  const totalGrossPnl = data.reduce((sum, d) => sum + d.pnl, 0);
  const totalFees = data.reduce((sum, d) => sum + (d.fees || 0), 0);
  const totalNetPnl = data.reduce((sum, d) => sum + (d.net_pnl !== undefined ? d.net_pnl : d.pnl), 0);
  const totalTrades = data.reduce((sum, d) => sum + d.trade_count, 0);
  const totalWins = data.reduce((sum, d) => sum + d.win_count, 0);
  const totalLosses = data.reduce((sum, d) => sum + d.loss_count, 0);
  const winRate = totalTrades > 0 ? ((totalWins / totalTrades) * 100).toFixed(1) : '0.0';
  const selectedDay = selectedDate ? data.find((day) => day.date === selectedDate) || null : null;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload as DailyData;
      const net = d.net_pnl !== undefined ? d.net_pnl : d.pnl;
      const fees = d.fees || 0;
      return (
        <div className="bg-card border border-border p-3 rounded-xl shadow-xl min-w-[180px]">
          <p className="text-xs font-bold text-muted-foreground mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground font-medium">총수익:</span>
              <span className={`font-black ${d.pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                {isPrivate ? '****' : `${d.pnl >= 0 ? '+' : ''}${d.pnl.toFixed(2)}`}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground font-medium">수수료:</span>
              <span className="font-black text-danger">
                {isPrivate ? '****' : `-${fees.toFixed(2)}`}
              </span>
            </div>
            <div className="flex justify-between text-sm border-t border-border/50 pt-1.5 mt-1.5">
              <span className="text-foreground font-bold">수익총계:</span>
              <span className={`font-black ${net >= 0 ? 'text-success' : 'text-danger'}`}>
                {isPrivate ? '****' : `${net >= 0 ? '+' : ''}${net.toFixed(2)} USDT`}
              </span>
            </div>
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-border/50 grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Trades</p>
              <p className="text-xs font-black">{d.trade_count}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Win/Loss</p>
              <p className="text-xs font-black text-success">{d.win_count} <span className="text-muted-foreground">/</span> <span className="text-danger">{d.loss_count}</span></p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-card p-6 rounded-3xl border border-border shadow-sm">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-foreground">성과 분석</h2>
          <p className="text-muted-foreground text-sm font-medium">선택한 월의 일별 수익 변동 및 통계를 확인합니다.</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="flex items-center gap-2 bg-muted/50 p-1 rounded-2xl border border-border max-w-full overflow-x-auto no-scrollbar">
            <Button 
              variant={mode === 'all' ? 'default' : 'ghost'} 
              size="sm" 
              className="rounded-xl font-bold text-[10px] h-8 px-4 shrink-0"
              onClick={() => setMode('all')}
            >
              전체
            </Button>
            <Button 
              variant={mode === 'mainnet' ? 'default' : 'ghost'} 
              size="sm" 
              className="rounded-xl font-bold text-[10px] h-8 px-4 shrink-0"
              onClick={() => setMode('mainnet')}
            >
              실전
            </Button>
            {!disable_all_testnet && (
              <Button 
                variant={mode === 'testnet' ? 'default' : 'ghost'} 
                size="sm" 
                className="rounded-xl font-bold text-[10px] h-8 px-4 shrink-0"
                onClick={() => setMode('testnet')}
              >
                테스트넷
              </Button>
            )}
            <Button 
              variant={mode === 'paper' ? 'default' : 'ghost'} 
              size="sm" 
              className="rounded-xl font-bold text-[10px] h-8 px-4 shrink-0"
              onClick={() => setMode('paper')}
            >
              시뮬레이션
            </Button>
          </div>
          <div className="flex items-center gap-2 rounded-2xl border border-border bg-background px-3 h-10">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-transparent text-sm font-bold text-foreground outline-none w-32"
            />
          </div>
          
          <Select value={selectedSymbol} onValueChange={setSelectedSymbol}>
            <SelectTrigger className="w-[140px] h-10 rounded-2xl border-border bg-background font-bold text-xs">
              <SelectValue placeholder="코인 선택" />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-border">
              <SelectItem value="all" className="text-xs font-bold">전체 코인</SelectItem>
              {availableSymbols.map(sym => (
                <SelectItem key={sym} value={sym} className="text-xs font-bold">{sym}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

          <Button
            variant="outline"
            size="sm"
            className="rounded-xl border-border bg-background"
            onClick={() => setIsExportModalOpen(true)}
          >
            <Download className="w-4 h-4 mr-1" />
            내보내기
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl border-border bg-background"
            onClick={fetchData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            갱신
          </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">선택 월 총수익</p>
          <p className={`text-lg sm:text-2xl font-black mt-1 ${totalGrossPnl >= 0 ? 'text-success' : 'text-danger'}`}>
             {isPrivate ? '****' : `${totalGrossPnl >= 0 ? '+' : ''}${totalGrossPnl.toFixed(2)}`} <span className="text-[10px] sm:text-xs opacity-50">USDT</span>
          </p>
        </Card>
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">선택 월 수수료</p>
          <p className="text-lg sm:text-2xl font-black text-danger mt-1">
             {isPrivate ? '****' : `-${totalFees.toFixed(2)}`} <span className="text-[10px] sm:text-xs opacity-50">USDT</span>
          </p>
        </Card>
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card border-primary/20 bg-primary/5">
          <p className="text-[10px] font-black text-primary uppercase tracking-widest">선택 월 수익총계 (Net)</p>
          <p className={`text-lg sm:text-2xl font-black mt-1 ${totalNetPnl >= 0 ? 'text-success' : 'text-danger'}`}>
             {isPrivate ? '****' : `${totalNetPnl >= 0 ? '+' : ''}${totalNetPnl.toFixed(2)}`} <span className="text-[10px] sm:text-xs opacity-50 font-bold">USDT</span>
          </p>
        </Card>
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">선택 월 총 거래</p>
          <p className="text-lg sm:text-2xl font-black text-foreground mt-1">{totalTrades} <span className="text-[10px] sm:text-xs text-muted-foreground font-bold">회</span></p>
        </Card>
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">평균 승률</p>
          <p className="text-lg sm:text-2xl font-black text-foreground mt-1">{winRate}%</p>
        </Card>
      </div>

      {/* Chart Section */}
      <Card className="rounded-3xl border border-border shadow-sm p-6 bg-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-xl text-primary">
              <TrendingUp className="w-4 h-4" />
            </div>
            <h3 className="font-black text-lg">일별 수익총계 변동 (Net PnL)</h3>
          </div>
        </div>
        <div className="h-[300px] w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              onClick={(state: any) => {
                const clickedDate = typeof state?.activeLabel === 'string' ? state.activeLabel : null;
                if (clickedDate) {
                  setSelectedDate(clickedDate);
                }
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.5} />
              <XAxis 
                dataKey="date" 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontWeight: 600 }}
                tickFormatter={(val) => val.split('-').slice(1).join('/')}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false} 
                tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontWeight: 600 }}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.4 }} />
              <ReferenceLine y={0} stroke="var(--border)" strokeWidth={2} />
              <Bar dataKey="net_pnl" radius={[4, 4, 0, 0]} barSize={12}>
                {data.map((entry, index) => {
                  const net = entry.net_pnl !== undefined ? entry.net_pnl : entry.pnl;
                  return <Cell key={`cell-${index}`} fill={net >= 0 ? '#10b981' : '#f43f5e'} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-[10px] text-muted-foreground text-center mt-4 font-bold uppercase tracking-widest">막대를 클릭하면 상세 내역을 볼 수 있습니다.</p>
      </Card>

      <Card className="rounded-2xl border border-border shadow-sm overflow-hidden bg-card">
        <div className="flex items-center justify-between p-5 border-b border-border bg-muted/20">
          <div>
            <h3 className="font-black text-base text-foreground">일별 요약 테이블</h3>
            <p className="text-[11px] text-muted-foreground font-medium">모바일에서는 아래 표에서 날짜를 눌러 상세 내역을 볼 수 있습니다.</p>
          </div>
        </div>
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead className="text-[10px] font-black uppercase">날짜</TableHead>
              <TableHead className="text-[10px] font-black uppercase text-right">총수익</TableHead>
              <TableHead className="text-[10px] font-black uppercase text-right">수수료</TableHead>
              <TableHead className="text-[10px] font-black uppercase text-right">수익총계</TableHead>
              <TableHead className="text-[10px] font-black uppercase text-right">Trade</TableHead>
              <TableHead className="text-[10px] font-black uppercase text-right">Win / Loss</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  표시할 일별 데이터가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              [...data].reverse().map((day) => {
                const isSelected = selectedDate === day.date;
                const net = day.net_pnl !== undefined ? day.net_pnl : day.pnl;
                const fees = day.fees || 0;

                return (
                  <TableRow
                    key={day.date}
                    onClick={() => setSelectedDate(day.date)}
                    className={`cursor-pointer ${isSelected ? 'bg-primary/5' : ''}`}
                  >
                    <TableCell className="font-bold text-foreground">{day.date}</TableCell>
                    <TableCell className={`text-right font-bold ${day.pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                      {isPrivate ? '****' : `${day.pnl >= 0 ? '+' : ''}${day.pnl.toFixed(2)}`}
                    </TableCell>
                    <TableCell className="text-right font-bold text-danger">
                      {isPrivate ? '****' : `-${fees.toFixed(2)}`}
                    </TableCell>
                    <TableCell className={`text-right font-black ${net >= 0 ? 'text-success' : 'text-danger'}`}>
                      {isPrivate ? '****' : `${net >= 0 ? '+' : ''}${net.toFixed(2)}`}
                    </TableCell>
                    <TableCell className="text-right font-bold text-foreground">
                      {day.trade_count}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      <span className="text-success">{day.win_count}</span>
                      <span className="mx-1 text-muted-foreground/70">/</span>
                      <span className="text-danger">{day.loss_count}</span>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Daily Breakdown */}
      {selectedDay && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-12">
          <div className="lg:col-span-4 space-y-4">
             <Card className="rounded-2xl border border-border shadow-sm p-5 bg-card h-full">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-black text-muted-foreground text-xs uppercase tracking-widest">선택일 정보</h4>
                  <Badge variant="outline" className="rounded-lg">{selectedDay.date}</Badge>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm font-medium text-muted-foreground">총 수익</span>
                    <span className={`text-base font-bold ${selectedDay.pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                      {isPrivate ? '****' : `${selectedDay.pnl >= 0 ? '+' : ''}${selectedDay.pnl.toFixed(2)} USDT`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm font-medium text-muted-foreground">총 수수료</span>
                    <span className="text-base font-bold text-danger">
                      {isPrivate ? '****' : `-${(selectedDay.fees || 0).toFixed(2)} USDT`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm font-black text-foreground">수익 총계 (Net)</span>
                    <span className={`text-lg font-black ${(selectedDay.net_pnl !== undefined ? selectedDay.net_pnl : selectedDay.pnl) >= 0 ? 'text-success' : 'text-danger'}`}>
                      {isPrivate ? '****' : `${(selectedDay.net_pnl !== undefined ? selectedDay.net_pnl : selectedDay.pnl) >= 0 ? '+' : ''}${(selectedDay.net_pnl !== undefined ? selectedDay.net_pnl : selectedDay.pnl).toFixed(2)} USDT`}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm font-medium text-muted-foreground">거래 횟수</span>
                    <span className="text-lg font-black">{selectedDay.trade_count}회</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border/50">
                    <span className="text-sm font-medium text-muted-foreground">익절/손절</span>
                    <div className="flex items-center gap-1.5 font-black">
                      <span className="text-success">{selectedDay.win_count}</span>
                      <span className="text-muted-foreground italic">/</span>
                      <span className="text-danger">{selectedDay.loss_count}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm font-medium text-muted-foreground">구동 봇 수</span>
                    <span className="text-lg font-black">{selectedDay.bots.length}개</span>
                  </div>
                </div>
             </Card>
          </div>
          <div className="lg:col-span-8">
            <Card className="rounded-2xl border border-border shadow-sm overflow-hidden bg-card">
              <Table>
                <TableHeader className="bg-muted/30">
                  <TableRow>
                    <TableHead className="text-[10px] font-black uppercase">봇 이름 / 심볼</TableHead>
                    <TableHead className="text-[10px] font-black uppercase text-right">거래수</TableHead>
                    <TableHead className="text-[10px] font-black uppercase text-right">총수익</TableHead>
                    <TableHead className="text-[10px] font-black uppercase text-right">수수료</TableHead>
                    <TableHead className="text-[10px] font-black uppercase text-right">수익총계</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedDay.bots.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                        해당 날짜에 종료된 거래가 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : (
                    [...selectedDay.bots].sort((a, b) => (b.net_pnl !== undefined ? b.net_pnl : b.pnl) - (a.net_pnl !== undefined ? a.net_pnl : a.pnl)).map((bot) => {
                      const bNet = bot.net_pnl !== undefined ? bot.net_pnl : bot.pnl;
                      const bFees = bot.fees || 0;
                      return (
                        <TableRow key={bot.bot_id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => onSelectBot?.(String(bot.bot_id))}
                                className="text-left group flex items-center gap-1.5"
                              >
                                <div className="text-xs font-black group-hover:text-primary transition-colors">
                                  #{bot.bot_id} {bot.name || 'Unknown Bot'}
                                </div>
                                <ArrowUpRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:text-primary transition-all transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                              </button>
                              {!bot.is_live ? (
                                <Badge variant="outline" className="text-[9px] h-4 px-1.5 bg-muted/50 border-dashed">Paper</Badge>
                              ) : bot.use_testnet ? (
                                <Badge variant="outline" className="text-[9px] h-4 px-1.5 bg-primary/10 text-primary border-none">Testnet</Badge>
                              ) : null}
                              {bot.archived && <Badge variant="secondary" className="text-[9px] h-4 px-1.5 bg-muted text-muted-foreground border-none">보관됨</Badge>}
                              {bot.disabled && <Badge variant="destructive" className="text-[9px] h-4 px-1.5 bg-danger/10 text-danger border-none">비활성</Badge>}
                            </div>
                            <p className="text-[10px] text-muted-foreground font-bold">{bot.symbol}</p>
                          </TableCell>
                          <TableCell className="text-right">
                            <p className="font-bold text-xs">{bot.trade_count}</p>
                            <p className="text-[10px] font-bold text-muted-foreground">
                              <span className="text-success">{bot.win_count}</span>
                              <span className="mx-1 text-muted-foreground/70">/</span>
                              <span className="text-danger">{bot.loss_count}</span>
                            </p>
                          </TableCell>
                          <TableCell className={`text-right font-bold text-xs ${bot.pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                            {isPrivate ? '****' : `${bot.pnl >= 0 ? '+' : ''}${bot.pnl.toFixed(2)}`}
                          </TableCell>
                          <TableCell className="text-right font-bold text-xs text-danger">
                            {isPrivate ? '****' : `-${bFees.toFixed(2)}`}
                          </TableCell>
                          <TableCell className={`text-right font-black text-xs ${bNet >= 0 ? 'text-success' : 'text-danger'}`}>
                            {isPrivate ? '****' : `${bNet >= 0 ? '+' : ''}${bNet.toFixed(2)} USDT`}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Card>
          </div>
        </div>
      )}
      {/* 성과 분석 내보내기 다이얼로그 */}
      {isExportModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-sm rounded-[32px] shadow-2xl border-none bg-card overflow-hidden">
            <CardHeader className="pb-4">
              <div className="flex justify-between items-center mb-1">
                <Badge variant="outline" className="text-[10px] font-black px-2 py-0.5 rounded-lg border-none bg-primary/10 text-primary uppercase">
                  PERFORMANCE EXPORT
                </Badge>
                <Button variant="ghost" size="sm" onClick={() => setIsExportModalOpen(false)} className="h-8 w-8 p-0 rounded-full hover:bg-muted"><X className="w-4 h-4" /></Button>
              </div>
              <CardTitle className="text-xl font-black flex items-center gap-2">
                성과 데이터 내보내기
              </CardTitle>
              <CardDescription className="text-xs font-medium">선택한 월의 일별 통계 데이터를 CSV 파일로 저장합니다.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <label className="text-[11px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> 내보낼 월 선택
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <Select value={exportYear} onValueChange={setExportYear}>
                    <SelectTrigger className="rounded-xl border-border bg-muted/20 font-bold h-11">
                      <SelectValue placeholder="연도" />
                    </SelectTrigger>
                    <SelectContent>
                      {["2024", "2025", "2026"].map(y => (
                        <SelectItem key={y} value={y}>{y}년</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={exportMonth} onValueChange={setExportMonth}>
                    <SelectTrigger className="rounded-xl border-border bg-muted/20 font-bold h-11">
                      <SelectValue placeholder="월" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 12}, (_, i) => String(i + 1).padStart(2, '0')).map(m => (
                        <SelectItem key={m} value={m}>{m}월</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-[11px] font-bold text-muted-foreground px-1">
                  <span>모드</span>
                  <span className="text-foreground">{mode === 'all' ? '전체' : (mode === 'mainnet' ? '실전' : (mode === 'testnet' ? '테스트넷' : '시뮬레이션'))}</span>
                </div>
                <div className="flex justify-between text-[11px] font-bold text-muted-foreground px-1">
                  <span>심볼 필터</span>
                  <span className="text-foreground">{selectedSymbol === 'all' ? '전체 코인' : selectedSymbol}</span>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex gap-3 px-6 pb-6 pt-0">
              <Button
                onClick={() => {
                  const url = `/dca/export_daily_stats?mode=${mode}&symbol=${selectedSymbol}&month=${exportYear}-${exportMonth}`;
                  window.location.href = url;
                  setIsExportModalOpen(false);
                }}
                className="flex-1 h-12 rounded-2xl font-black bg-primary text-white hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
              >
                다운로드 시작
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
};
